import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TableContainer,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  AdminCustomer,
  AdminFeatureFlag,
  AdminInvitation,
  AdminLicense,
  AdminUser,
  CustomerFeatureFlagOverride,
  adminAssignLicense,
  adminBatchCreateLicenses,
  adminBulkRenewLicenses,
  adminCreateCustomer,
  adminCreateInvitation,
  adminDeleteCustomerFeatureFlag,
  adminListCustomerFeatureFlags,
  adminListCustomers,
  adminListFeatureFlags,
  adminListLicenses,
  adminListUsers,
  adminRevokeLicense,
  adminSetCustomerDefaultNetwork,
  adminUpdateUser,
  adminUpsertCustomerFeatureFlag,
  readAxiosDetail,
} from './api_calls';
import NetworkFeedSelect from '../network/NetworkFeedSelect';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
// ─── Feature Flag Categories ──────────────────────────────────────────────────

interface Category {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  flags: string[];
}

function buildCategories(t: TFunction): Category[] {
  return [
    {
      id: 'simulation',
      label: t('customers.categories.simulation'),
      icon: <Icon name="chart-bar" size={20} />,
      color: '#1976d2',
      flags: [
        'clustering_ui',
        'simulations',
        'simulation_results_analytics',
        'simulation_results_sharing',
        'simulation_clustering',
        'create_service_from_simulation',
      ],
    },
    {
      id: 'ai',
      label: t('customers.categories.ai'),
      icon: <Icon d={ICON_FALLBACKS.AutoAwesome} size={20} />,
      color: '#7b1fa2',
      flags: ['ai_chat_agent', 'ai_fleet_generator', 'ai_trip_generator'],
    },
    {
      id: 'routes',
      label: t('customers.categories.routes'),
      icon: <Icon name="bus" size={20} />,
      color: '#388e3c',
      flags: [
        'fixed_routes',
        'generate_rides',
        'graphhopper_routing',
        'here_routing',
        'openroute_routing',
        'auto_generate_fixed_route_fleet',
        'rules_engine',
        'rules_engine_admin',
        'rules_engine_override',
        'rules_engine_driver_model',
        'advance_rostering',
      ],
    },
    {
      id: 'analysis',
      label: t('customers.categories.analysis'),
      icon: <Icon name="map" size={20} />,
      color: '#f57c00',
      flags: [
        'population_analysis',
        'spatial_analysis',
        'meeting_points_generator',
        'attribute_table',
      ],
    },
    {
      id: 'integrations',
      label: t('customers.categories.integrations'),
      icon: <Icon d={ICON_FALLBACKS.IntegrationInstructions} size={20} />,
      color: '#c62828',
      flags: [
        'import_gtfs',
        'import_osm_locations',
        'import_trips',
        'import_kml',
      ],
    },
    {
      id: 'platform',
      label: t('customers.categories.platform'),
      icon: <Icon d={ICON_FALLBACKS.DashboardCustomize} size={20} />,
      color: '#00838f',
      flags: [
        'map_save_load',
        'google_basemap',
        'data_transfer_drawer',
        'environment_tokens',
        'environment_selector',
        'jira_integration',
        'rides_transfer',
        'upload_routes_to_uat',
        'profile_page',
        'tools_page',
        'whats_new_overlay',
      ],
    },
  ];
}

const CATEGORY_FLAG_MAP: Record<string, string[]> = {
  simulation: [
    'clustering_ui',
    'simulations',
    'simulation_results_analytics',
    'simulation_results_sharing',
    'simulation_clustering',
    'create_service_from_simulation',
  ],
  ai: ['ai_chat_agent', 'ai_fleet_generator', 'ai_trip_generator'],
  routes: [
    'fixed_routes',
    'generate_rides',
    'graphhopper_routing',
    'here_routing',
    'openroute_routing',
    'auto_generate_fixed_route_fleet',
    'rules_engine',
    'rules_engine_admin',
    'rules_engine_override',
    'rules_engine_driver_model',
    'advance_rostering',
  ],
  analysis: ['population_analysis', 'spatial_analysis', 'meeting_points_generator', 'attribute_table'],
  integrations: ['import_gtfs', 'import_osm_locations', 'import_trips', 'import_kml'],
  platform: [
    'map_save_load',
    'google_basemap',
    'data_transfer_drawer',
    'environment_tokens',
    'environment_selector',
    'jira_integration',
    'rides_transfer',
    'upload_routes_to_uat',
    'profile_page',
    'tools_page',
    'whats_new_overlay',
  ],
};

const UNCATEGORISED_ID = 'other';

const BASIC_BASELINE_FLAG_NAMES = [
  'map_save_load',
  'whats_new_overlay',
  'simulations',
  'simulation_results_analytics',
  'generate_rides',
  'population_analysis',
  'attribute_table',
  'meeting_points_generator',
  'import_gtfs',
  'import_osm_locations',
  'import_kml',
] as const;

const ALWAYS_ENABLED_FLAG_NAMES = ['whats_new_overlay'] as const;

const UPLOAD_ADDON_FLAG_NAMES = ['import_trips'] as const;

const FIXED_ROUTE_ADDON_FLAG_NAMES = [
  'fixed_routes',
  'auto_generate_fixed_route_fleet',
  'here_routing',
  'openroute_routing',
] as const;

const LEGACY_GROUP_FLAG_NAMES = ['group_simulations', 'group_fixed_routes'] as const;
const HIDDEN_FLAG_NAMES = [
  'group_simulations',
  'group_fixed_routes',
  'isochrone_generator',
  'autopilot_dashboard',
  'fleet_optimisation_tool',
  'route_optimisation',
  'fixed_route_optimiser',
  'demographics',
  'population_data_overlay',
  'heatmap_layer',
  'ride_user_assignment',
  'transit_scan',
] as const;

function isBaselineFlag(name: string): boolean {
  return (BASIC_BASELINE_FLAG_NAMES as readonly string[]).includes(name);
}

function isAlwaysEnabledFlag(name: string): boolean {
  return (ALWAYS_ENABLED_FLAG_NAMES as readonly string[]).includes(name);
}

function getCategoryForFlag(flagName: string): string {
  for (const catId of Object.keys(CATEGORY_FLAG_MAP)) {
    if (CATEGORY_FLAG_MAP[catId].includes(flagName)) return catId;
  }
  return UNCATEGORISED_ID;
}

// ─── License Status Helper ────────────────────────────────────────────────────

function getLicenseStatus(
  lic: AdminLicense,
  t: TFunction,
): { label: string; color: 'success' | 'warning' | 'error' | 'default' } {
  if (!lic.user_id) return { label: t('licenses.status.unassigned'), color: 'default' };
  const now = Date.now();
  const exp = new Date(lic.expires_at).getTime();
  if (exp < now) return { label: t('licenses.status.expired'), color: 'error' };
  const daysLeft = (exp - now) / (1000 * 60 * 60 * 24);
  if (daysLeft < 30) return { label: t('licenses.status.expiringSoon'), color: 'warning' };
  return { label: t('licenses.status.active'), color: 'success' };
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CustomersTab(): React.ReactElement {
  type LicenseSortKey = 'user' | 'expires' | 'status';
  const { t } = useTranslation(['admin', 'common']);
  const CATEGORIES = React.useMemo(() => buildCategories(t), [t]);
  const inviteBlockedMessage = t('customers.inviteBlockedMessage');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<CustomerFeatureFlagOverride[]>([]);
  const [globalFlags, setGlobalFlags] = useState<AdminFeatureFlag[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [accessApplySummary, setAccessApplySummary] = useState<string | null>(null);
  const [isApplyingAccessPolicy, setIsApplyingAccessPolicy] = useState(false);
  const [activeFlagsTab, setActiveFlagsTab] = useState<string>('simulation');
  const [inactiveFlagsTab, setInactiveFlagsTab] = useState<string>('simulation');

  const [addOpen, setAddOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [savingDefaultNetwork, setSavingDefaultNetwork] = useState(false);

  // Customer users state
  const [customerUsers, setCustomerUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Add user to customer dialog
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [unassignedUsers, setUnassignedUsers] = useState<AdminUser[]>([]);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState<AdminUser | null>(null);
  const [addUserSaving, setAddUserSaving] = useState(false);

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'company_admin'>('member');
  const [inviteExpiry, setInviteExpiry] = useState(7);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [createdInvitation, setCreatedInvitation] = useState<AdminInvitation | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);

  // Licenses state
  const [customerLicenses, setCustomerLicenses] = useState<AdminLicense[]>([]);
  const [loadingLicenses, setLoadingLicenses] = useState(false);
  const [addLicensesOpen, setAddLicensesOpen] = useState(false);
  const [addLicensesCount, setAddLicensesCount] = useState(1);
  const [addLicensesExpiry, setAddLicensesExpiry] = useState('');
  const [addLicensesSaving, setAddLicensesSaving] = useState(false);
  const [renewAllOpen, setRenewAllOpen] = useState(false);
  const [renewAllExpiry, setRenewAllExpiry] = useState('');
  const [renewAllSaving, setRenewAllSaving] = useState(false);
  const [assignLicenseTarget, setAssignLicenseTarget] = useState<AdminLicense | null>(null);
  const [assignLicenseUsers, setAssignLicenseUsers] = useState<AdminUser[]>([]);
  const [assignLicenseUserId, setAssignLicenseUserId] = useState('');
  const [assignLicenseLoading, setAssignLicenseLoading] = useState(false);
  const [assignLicenseSaving, setAssignLicenseSaving] = useState(false);
  const [licenseSearch, setLicenseSearch] = useState('');
  const [licenseSortBy, setLicenseSortBy] = useState<LicenseSortKey>('user');
  const [licenseSortDirection, setLicenseSortDirection] = useState<'asc' | 'desc'>('asc');

  const loadCustomers = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const list = await adminListCustomers();
      setCustomers(list);
      setSelectedId((prev) => {
        if (prev && list.some((c) => c.customer_id === prev)) return prev;
        return list[0]?.customer_id ?? null;
      });
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadGlobals = useCallback(async () => {
    try {
      const flags = await adminListFeatureFlags();
      const hidden = new Set<string>(HIDDEN_FLAG_NAMES);
      setGlobalFlags(flags.filter((flag) => !hidden.has(flag.name)));
    } catch (e) {
      setError(readAxiosDetail(e));
    }
  }, []);

  const loadOverrides = useCallback(async () => {
    if (!selectedId) {
      setOverrides([]);
      return;
    }
    setLoadingOverrides(true);
    setError(null);
    try {
      const rows = await adminListCustomerFeatureFlags(selectedId);
      setOverrides(rows);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoadingOverrides(false);
    }
  }, [selectedId]);

  useEffect(() => {
    void loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    void loadGlobals();
  }, [loadGlobals]);

  useEffect(() => {
    void loadOverrides();
  }, [loadOverrides]);

  const loadCustomerUsers = useCallback(async () => {
    if (!selectedId) {
      setCustomerUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const users = await adminListUsers({ customer_id: selectedId });
      setCustomerUsers(users);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoadingUsers(false);
    }
  }, [selectedId]);

  useEffect(() => {
    void loadCustomerUsers();
  }, [loadCustomerUsers]);

  const loadCustomerLicenses = useCallback(async () => {
    if (!selectedId) {
      setCustomerLicenses([]);
      return;
    }
    setLoadingLicenses(true);
    try {
      const rows = await adminListLicenses({ customer_id: selectedId });
      setCustomerLicenses(rows);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoadingLicenses(false);
    }
  }, [selectedId]);

  useEffect(() => {
    void loadCustomerLicenses();
  }, [loadCustomerLicenses]);

  useEffect(() => {
    if (customerLicenses.length > 0) {
      setWarning(null);
    }
  }, [customerLicenses.length]);

  const openAddUserDialog = async () => {
    setAddUserOpen(true);
    setAddUserLoading(true);
    setSelectedUserToAdd(null);
    try {
      const allUsers = await adminListUsers();
      const available = allUsers.filter((u) => !u.customer_id);
      setUnassignedUsers(available);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleAddUserToCustomer = async () => {
    if (!selectedId || !selectedUserToAdd) return;
    setAddUserSaving(true);
    setError(null);
    try {
      await adminUpdateUser(selectedUserToAdd.user_id, { customer_id: selectedId });
      setAddUserOpen(false);
      setSelectedUserToAdd(null);
      await loadCustomerUsers();
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setAddUserSaving(false);
    }
  };

  const handleRemoveUserFromCustomer = async (userId: string) => {
    setError(null);
    try {
      await adminUpdateUser(userId, { customer_id: null });
      await loadCustomerUsers();
    } catch (e) {
      setError(readAxiosDetail(e));
    }
  };

  // Set the customer's org-wide default transit network (cascades to its users on sign-in).
  const handleSetDefaultNetwork = async (feedId: string | null) => {
    if (!selectedId) return;
    const previous = customers.find((c) => c.customer_id === selectedId)?.default_network_feed_id ?? null;
    setCustomers((list) =>
      list.map((c) => (c.customer_id === selectedId ? { ...c, default_network_feed_id: feedId } : c)),
    );
    setSavingDefaultNetwork(true);
    setError(null);
    try {
      const updated = await adminSetCustomerDefaultNetwork(selectedId, feedId);
      setCustomers((list) =>
        list.map((c) =>
          c.customer_id === selectedId ? { ...c, default_network_feed_id: updated.default_network_feed_id ?? null } : c,
        ),
      );
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.default_network_changed, {
        scope: 'customer',
        feed_id: feedId,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      setCustomers((list) =>
        list.map((c) => (c.customer_id === selectedId ? { ...c, default_network_feed_id: previous } : c)),
      );
      setError(readAxiosDetail(e));
    } finally {
      setSavingDefaultNetwork(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) return;
    setCreateLoading(true);
    setError(null);
    try {
      const c = await adminCreateCustomer(newCustomerName.trim());
      setCustomers((prev) => [...prev, c]);
      setSelectedId(c.customer_id);
      setAddOpen(false);
      setNewCustomerName('');
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setCreateLoading(false);
    }
  };

  // ─── Feature flag handlers ────────────────────────────────────────────────

  const getEffectiveEnabled = useCallback((flag: AdminFeatureFlag): boolean => {
    if (isAlwaysEnabledFlag(flag.name)) return true;
    const override = overrides.find((o) => o.feature_flag_id === flag.feature_flag_id);
    return override ? override.enabled : flag.enabled;
  }, [overrides]);

  const getOverrideForFlag = useCallback((flag: AdminFeatureFlag): CustomerFeatureFlagOverride | undefined => {
    return overrides.find((o) => o.feature_flag_id === flag.feature_flag_id);
  }, [overrides]);

  const globalFlagByName = React.useMemo(() => {
    const m = new Map<string, AdminFeatureFlag>();
    for (const flag of globalFlags) {
      m.set(flag.name, flag);
    }
    return m;
  }, [globalFlags]);

  const getEffectiveEnabledByName = useCallback(
    (flagName: string): boolean => {
      const flag = globalFlagByName.get(flagName);
      if (!flag) return false;
      return getEffectiveEnabled(flag);
    },
    [globalFlagByName, getEffectiveEnabled]
  );

  const applyAccessPolicy = useCallback(
    async (
      levelName: string,
      opts: {
        enableUploadAddon: boolean;
        enableFixedRouteAddon: boolean;
      }
    ) => {
      if (!selectedId) return;
      setError(null);
      setAccessApplySummary(null);
      setIsApplyingAccessPolicy(true);

      const requestedEnableNames = new Set<string>([...BASIC_BASELINE_FLAG_NAMES]);

      if (opts.enableUploadAddon) {
        for (const flagName of UPLOAD_ADDON_FLAG_NAMES) requestedEnableNames.add(flagName);
      }
      if (opts.enableFixedRouteAddon) {
        for (const flagName of FIXED_ROUTE_ADDON_FLAG_NAMES) requestedEnableNames.add(flagName);
      }

      const requestedDisableNames = new Set<string>();
      if (!opts.enableUploadAddon) {
        for (const flagName of UPLOAD_ADDON_FLAG_NAMES) requestedDisableNames.add(flagName);
      }
      if (!opts.enableFixedRouteAddon) {
        for (const flagName of FIXED_ROUTE_ADDON_FLAG_NAMES) requestedDisableNames.add(flagName);
      }
      for (const enabledName of Array.from(requestedEnableNames)) {
        requestedDisableNames.delete(enabledName);
      }

      const missingNames = new Set<string>();
      const operations: Array<Promise<unknown>> = [];
      let enabledCount = 0;
      let disabledCount = 0;

      for (const flagName of Array.from(requestedEnableNames)) {
        const flag = globalFlagByName.get(flagName);
        if (!flag) {
          missingNames.add(flagName);
          continue;
        }
        const currentEnabled = getEffectiveEnabled(flag);
        if (currentEnabled) continue;
        const existing = overrides.find((o) => o.feature_flag_id === flag.feature_flag_id);
        operations.push(
          adminUpsertCustomerFeatureFlag(selectedId, {
            feature_flag_id: flag.feature_flag_id,
            enabled: true,
            config: existing?.config ?? {},
          })
        );
        enabledCount += 1;
      }

      for (const flagName of Array.from(requestedDisableNames)) {
        const flag = globalFlagByName.get(flagName);
        if (!flag) {
          missingNames.add(flagName);
          continue;
        }
        const currentEnabled = getEffectiveEnabled(flag);
        if (!currentEnabled) continue;
        const existing = overrides.find((o) => o.feature_flag_id === flag.feature_flag_id);
        operations.push(
          adminUpsertCustomerFeatureFlag(selectedId, {
            feature_flag_id: flag.feature_flag_id,
            enabled: false,
            config: existing?.config ?? {},
          })
        );
        disabledCount += 1;
      }

      const legacyGroupOverrides = overrides.filter(
        (o) =>
          LEGACY_GROUP_FLAG_NAMES.includes(o.flag_name as (typeof LEGACY_GROUP_FLAG_NAMES)[number]) ||
          o.flag_name.startsWith('group_')
      );

      for (const override of legacyGroupOverrides) {
        operations.push(adminDeleteCustomerFeatureFlag(selectedId, override.feature_flag_id));
      }

      try {
        await Promise.all(operations);
        await loadOverrides();
        const missingCount = missingNames.size;
        const deletedGroupCount = legacyGroupOverrides.length;
        setAccessApplySummary(
          t('customers.features.applySummary', {
            level: levelName,
            enabled: enabledCount,
            disabled: disabledCount,
            deleted: deletedGroupCount,
            deletedNoun: t('customers.features.overrideNoun', { count: deletedGroupCount }),
            missing: missingCount,
            missingNoun: t('customers.features.flagNoun', { count: missingCount }),
          })
        );
      } catch (e) {
        setError(readAxiosDetail(e));
      } finally {
        setIsApplyingAccessPolicy(false);
      }
    },
    [selectedId, globalFlagByName, overrides, loadOverrides, getEffectiveEnabled, t]
  );

  const handleToggleFlag = async (flag: AdminFeatureFlag) => {
    if (!selectedId) return;
    setError(null);
    setAccessApplySummary(null);
    const override = overrides.find((o) => o.feature_flag_id === flag.feature_flag_id);
    const currentEnabled = override ? override.enabled : flag.enabled;
    const nextEnabled = !currentEnabled;
    if (isBaselineFlag(flag.name) && !nextEnabled) {
      setError(t('customers.features.baselineDisableError', { flag: flag.name }));
      return;
    }
    try {
      await adminUpsertCustomerFeatureFlag(selectedId, {
        feature_flag_id: flag.feature_flag_id,
        enabled: nextEnabled,
        config: override?.config ?? {},
      });
      await loadOverrides();
    } catch (e) {
      setError(readAxiosDetail(e));
    }
  };

  const handleToggleCategoryFlags = async (categoryFlags: AdminFeatureFlag[], enable: boolean) => {
    if (!selectedId) return;
    setError(null);
    setAccessApplySummary(null);
    try {
      const operations = categoryFlags
        .map((flag) => {
          const existing = overrides.find((o) => o.feature_flag_id === flag.feature_flag_id);
          const currentEnabled = existing ? existing.enabled : flag.enabled;
          const targetEnabled = isBaselineFlag(flag.name) ? true : enable;
          if (currentEnabled === targetEnabled) return null;
          return adminUpsertCustomerFeatureFlag(selectedId, {
            feature_flag_id: flag.feature_flag_id,
            enabled: targetEnabled,
            config: existing?.config ?? {},
          });
        })
        .filter((op): op is Promise<CustomerFeatureFlagOverride> => op != null);
      if (operations.length === 0) return;
      await Promise.all(
        operations
      );
      await loadOverrides();
    } catch (e) {
      setError(readAxiosDetail(e));
    }
  };

  // ─── Invite handlers ──────────────────────────────────────────────────────

  const openInviteDialog = () => {
    if (customerLicenses.length === 0) {
      setWarning(inviteBlockedMessage);
      return;
    }
    setInviteEmail('');
    setInviteRole('member');
    setInviteExpiry(7);
    setCreatedInvitation(null);
    setInviteCopied(false);
    setInviteOpen(true);
  };

  const closeInviteDialog = () => {
    setInviteOpen(false);
    setCreatedInvitation(null);
    setInviteCopied(false);
  };

  const handleCreateInvitation = async () => {
    if (!selectedId || !inviteEmail.trim()) return;
    setInviteSaving(true);
    setError(null);
    try {
      const inv = await adminCreateInvitation(selectedId, {
        email: inviteEmail.trim(),
        company_role: inviteRole,
        expires_in_days: inviteExpiry,
      });
      setCreatedInvitation(inv);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setInviteSaving(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!createdInvitation) return;
    const url = `${window.location.origin}/register?token=${createdInvitation.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  // ─── License handlers ───────────────────────────────────────────────────

  const handleBatchCreate = async () => {
    if (!selectedId || !addLicensesExpiry.trim() || addLicensesCount < 1) return;
    setAddLicensesSaving(true);
    setError(null);
    try {
      await adminBatchCreateLicenses(selectedId, {
        count: addLicensesCount,
        expires_at: addLicensesExpiry.trim(),
      });
      setAddLicensesOpen(false);
      await loadCustomerLicenses();
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setAddLicensesSaving(false);
    }
  };

  const handleBulkRenew = async () => {
    if (!selectedId || !renewAllExpiry.trim()) return;
    setRenewAllSaving(true);
    setError(null);
    try {
      await adminBulkRenewLicenses(selectedId, renewAllExpiry.trim());
      setRenewAllOpen(false);
      await loadCustomerLicenses();
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setRenewAllSaving(false);
    }
  };

  const handleDeleteLicense = async (licenseId: string) => {
    setError(null);
    try {
      await adminRevokeLicense(licenseId);
      await loadCustomerLicenses();
    } catch (e) {
      setError(readAxiosDetail(e));
    }
  };

  const openAssignLicenseDialog = async (lic: AdminLicense) => {
    setAssignLicenseTarget(lic);
    setAssignLicenseUserId('');
    setAssignLicenseLoading(true);
    try {
      const users = await adminListUsers({ customer_id: lic.customer_id });
      const assignedUserIds = new Set(customerLicenses.filter((l) => l.user_id).map((l) => l.user_id));
      const available = users.filter((u) => !assignedUserIds.has(u.user_id));
      setAssignLicenseUsers(available);
      if (available[0]) setAssignLicenseUserId(available[0].user_id);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setAssignLicenseLoading(false);
    }
  };

  const handleAssignLicense = async () => {
    if (!assignLicenseTarget || !assignLicenseUserId) return;
    setAssignLicenseSaving(true);
    setError(null);
    try {
      await adminAssignLicense(assignLicenseTarget.license_id, assignLicenseUserId);
      setAssignLicenseTarget(null);
      await loadCustomerLicenses();
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setAssignLicenseSaving(false);
    }
  };

  const handleOpenNextLicenseToAssign = async () => {
    const nextLicense = sortedCustomerLicenses.find((lic) => !lic.user_id);
    if (!nextLicense) {
      setError(t('customers.licensesSection.noUnassignedAvailable'));
      return;
    }
    await openAssignLicenseDialog(nextLicense);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const selectedCustomer = customers.find((c) => c.customer_id === selectedId);
  const inviteDisabledForNoLicenses = customerLicenses.length === 0;

  // Group global flags by category for the feature cards
  const flagsByCategory: Record<string, AdminFeatureFlag[]> = {};
  for (const flag of globalFlags) {
    const catId = getCategoryForFlag(flag.name);
    if (!flagsByCategory[catId]) flagsByCategory[catId] = [];
    flagsByCategory[catId].push(flag);
  }
  const uncategorised = flagsByCategory[UNCATEGORISED_ID] ?? [];
  const categoryTabs: Category[] = [
    ...CATEGORIES,
    {
      id: UNCATEGORISED_ID,
      label: t('customers.categories.other'),
      icon: <Icon d={ICON_FALLBACKS.AltRoute} size={20} />,
      color: '#616161',
      flags: [],
    },
  ];
  const flagsByCategoryWithOther: Record<string, AdminFeatureFlag[]> = {
    ...flagsByCategory,
    [UNCATEGORISED_ID]: uncategorised,
  };
  const getFlagsForTab = (tabId: string, enabled: boolean): AdminFeatureFlag[] =>
    (flagsByCategoryWithOther[tabId] ?? []).filter((flag) => getEffectiveEnabled(flag) === enabled);
  const activeTabCategory = categoryTabs.find((cat) => cat.id === activeFlagsTab) ?? categoryTabs[0];
  const inactiveTabCategory = categoryTabs.find((cat) => cat.id === inactiveFlagsTab) ?? categoryTabs[0];
  const activeTabFlags = getFlagsForTab(activeTabCategory.id, true);
  const inactiveTabFlags = getFlagsForTab(inactiveTabCategory.id, false);
  const hasAnyActiveFlags = globalFlags.some((flag) => getEffectiveEnabled(flag));
  const hasAnyInactiveFlags = globalFlags.some((flag) => !getEffectiveEnabled(flag));
  const isUploadAddonEnabled = (UPLOAD_ADDON_FLAG_NAMES as readonly string[]).every((flagName) =>
    getEffectiveEnabledByName(flagName)
  );
  const isFixedRouteAddonEnabled = (FIXED_ROUTE_ADDON_FLAG_NAMES as readonly string[]).every((flagName) =>
    getEffectiveEnabledByName(flagName)
  );
  const policyPillSx = (isActive: boolean) => ({
    bgcolor: isActive ? 'success.main' : 'grey.300',
    color: isActive ? 'common.white' : 'text.primary',
    borderColor: isActive ? 'success.dark' : 'grey.500',
    '&:hover': {
      bgcolor: 'warning.main',
      color: 'common.white',
      borderColor: 'warning.dark',
    },
  });
  const sortedCustomerLicenses = useMemo(() => {
    const statusRank: Record<string, number> = {
      active: 0,
      expiring_soon: 1,
      expired: 2,
      unassigned: 3,
    };

    return [...customerLicenses].sort((a, b) => {
      let cmp = 0;
      if (licenseSortBy === 'user') {
        const aEmail = (a.user_email ?? '').trim();
        const bEmail = (b.user_email ?? '').trim();
        const aHasEmail = aEmail.length > 0;
        const bHasEmail = bEmail.length > 0;
        if (aHasEmail !== bHasEmail) return aHasEmail ? -1 : 1;

        cmp = aEmail.localeCompare(bEmail, undefined, { sensitivity: 'base' });
      } else if (licenseSortBy === 'expires') {
        cmp = new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime();
      } else {
        const aStatus = getLicenseStatus(a, t).label.toLowerCase().replace(' ', '_');
        const bStatus = getLicenseStatus(b, t).label.toLowerCase().replace(' ', '_');
        cmp = (statusRank[aStatus] ?? 99) - (statusRank[bStatus] ?? 99);
      }

      if (cmp === 0) {
        const byExpiry = new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime();
        if (byExpiry !== 0) cmp = byExpiry;
      }
      if (cmp === 0) cmp = a.license_id.localeCompare(b.license_id);
      return licenseSortDirection === 'asc' ? cmp : -cmp;
    });
  }, [customerLicenses, licenseSortBy, licenseSortDirection, t]);

  const filteredCustomerLicenses = useMemo(() => {
    const search = licenseSearch.trim().toLowerCase();

    return sortedCustomerLicenses.filter((lic) => {
      const normalizedEmail = (lic.user_email ?? '').trim().toLowerCase();
      const status = getLicenseStatus(lic, t).label.toLowerCase();
      const expiresDateText = new Date(lic.expires_at).toLocaleDateString().toLowerCase();

      if (search) {
        const searchBucket = `${normalizedEmail} ${status} ${expiresDateText} ${lic.license_id}`.trim();
        if (!searchBucket.includes(search)) return false;
      }

      return true;
    });
  }, [licenseSearch, sortedCustomerLicenses, t]);

  if (loadingList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {warning && (
        <Alert severity="warning" onClose={() => setWarning(null)} sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button startIcon={<Icon size={20} name="plus" />} variant="outlined" size="small" onClick={() => setAddOpen(true)}>
          {t('customers.newCustomer')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, minHeight: 360 }}>
        <Box
          sx={{
            width: { xs: '100%', md: 260 },
            flexShrink: 0,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: { xs: 'none', md: 600 },
            display: { xs: selectedId ? 'none' : 'block', md: 'block' },
          }}
        >
          <List dense disablePadding>
            {customers.map((c) => (
              <ListItemButton
                key={c.customer_id}
                selected={c.customer_id === selectedId}
                onClick={() => setSelectedId(c.customer_id)}
              >
                <ListItemText primary={c.name} secondary={c.customer_id} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: { xs: selectedId ? 'block' : 'none', md: 'block' } }}>
          {!selectedId ? (
            <Typography color="text.secondary">{t('customers.selectPrompt')}</Typography>
          ) : (
            <>
              {isMobile && (
                <Button
                  size="small"
                  startIcon={<Icon size={20} name="arrow-left" />}
                  onClick={() => setSelectedId(null)}
                  sx={{ mb: 1 }}
                >
                  {t('customers.allCustomers')}
                </Button>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedCustomer?.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icon size={20} name="user" />}
                    onClick={() => void openAddUserDialog()}
                  >
                    {t('customers.addUser')}
                  </Button>
                  {inviteDisabledForNoLicenses ? (
                    <Tooltip title={inviteBlockedMessage}>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => setWarning(inviteBlockedMessage)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setWarning(inviteBlockedMessage);
                          }
                        }}
                      >
                        <Button size="small" variant="outlined" startIcon={<Icon size={20} name="user" />} disabled>
                          {t('customers.inviteNew')}
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon size={20} name="user" />}
                      onClick={openInviteDialog}
                    >
                      {t('customers.inviteNew')}
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Default transit network — cascades to this customer's users on sign-in */}
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
              >
                <Icon name="bus" size={18} />
                {t('customers.defaultNetwork.heading')}
              </Typography>
              <Box sx={{ maxWidth: 480, mb: 2 }}>
                <NetworkFeedSelect
                  label={t('customers.defaultNetwork.selectLabel')}
                  value={selectedCustomer?.default_network_feed_id ?? null}
                  onChange={handleSetDefaultNetwork}
                  disabled={savingDefaultNetwork}
                  noneLabel={t('customers.defaultNetwork.none')}
                  helperText={t('customers.defaultNetwork.help')}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Users section */}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {t('customers.usersHeading', { count: customerUsers.length })}
              </Typography>
              {loadingUsers ? (
                <CircularProgress size={24} sx={{ mb: 2 }} />
              ) : customerUsers.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('customers.noUsersAssigned')}
                </Typography>
              ) : (
                <TableContainer sx={{ overflowX: 'auto', mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('customers.userColumns.email')}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('customers.userColumns.name')}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('customers.userColumns.role')}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('customers.userColumns.status')}</TableCell>
                        <TableCell align="right">{t('customers.userColumns.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customerUsers.map((u) => (
                        <TableRow key={u.user_id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {u.email}
                            </Typography>
                            {/* xs-only fold: name + role/status under email */}
                            <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 0.25 }}>
                              {u.full_name && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {u.full_name}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={u.role === 'admin' ? t('users.role.admin') : t('users.role.user')}
                                size="small"
                                color={u.role === 'admin' ? 'primary' : 'default'}
                                variant="outlined"
                              />
                              <Chip
                                label={u.disabled ? t('users.status.disabled') : t('users.status.active')}
                                size="small"
                                color={u.disabled ? 'warning' : 'success'}
                                variant="outlined"
                              />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2">{u.full_name || '—'}</Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Chip
                              label={u.role === 'admin' ? t('users.role.admin') : t('users.role.user')}
                              size="small"
                              color={u.role === 'admin' ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Chip
                              label={u.disabled ? t('users.status.disabled') : t('users.status.active')}
                              size="small"
                              color={u.disabled ? 'warning' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Icon size={20} name="minus" />}
                              onClick={() => void handleRemoveUserFromCustomer(u.user_id)}
                            >
                              {t('common.remove')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Features section */}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                {t('customers.features.heading')}
              </Typography>
              {accessApplySummary && (
                <Alert severity="success" onClose={() => setAccessApplySummary(null)} sx={{ mb: 2 }}>
                  {accessApplySummary}
                </Alert>
              )}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('customers.features.accessPolicy')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('customers.features.toolsNote')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <ButtonGroup
                      size="small"
                      variant="outlined"
                      aria-label={t('customers.features.uploadModeAriaLabel')}
                      sx={{
                        '& .MuiButton-root': { borderRadius: 0 },
                        '& .MuiButton-root:first-of-type': {
                          borderTopLeftRadius: 999,
                          borderBottomLeftRadius: 999,
                        },
                        '& .MuiButton-root:last-of-type': {
                          borderTopRightRadius: 999,
                          borderBottomRightRadius: 999,
                        },
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<Icon size={20} name="house" />}
                        disabled={isApplyingAccessPolicy}
                        sx={policyPillSx(!isUploadAddonEnabled)}
                        onClick={() =>
                          void applyAccessPolicy(t('customers.features.switchToBasic'), {
                            enableUploadAddon: false,
                            enableFixedRouteAddon: isFixedRouteAddonEnabled,
                          })
                        }
                      >
                        {t('customers.features.basic')}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Icon size={20} name="file-arrow-up" />}
                        disabled={isApplyingAccessPolicy}
                        sx={policyPillSx(isUploadAddonEnabled)}
                        onClick={() =>
                          void applyAccessPolicy(t('customers.features.switchToUpload'), {
                            enableUploadAddon: true,
                            enableFixedRouteAddon: isFixedRouteAddonEnabled,
                          })
                        }
                      >
                        {t('customers.features.upload')}
                      </Button>
                    </ButtonGroup>
                    <Button
                      size="small"
                      variant={isFixedRouteAddonEnabled ? 'contained' : 'outlined'}
                      startIcon={<Icon d={ICON_FALLBACKS.AltRoute} size={20} />}
                      sx={{
                        borderRadius: 999,
                        ...policyPillSx(isFixedRouteAddonEnabled),
                      }}
                      disabled={isApplyingAccessPolicy}
                      onClick={() =>
                        void applyAccessPolicy(
                          isFixedRouteAddonEnabled
                            ? t('customers.features.disableFixedRoute')
                            : t('customers.features.enableFixedRoute'),
                          {
                            enableUploadAddon: isUploadAddonEnabled,
                            enableFixedRouteAddon: !isFixedRouteAddonEnabled,
                          }
                        )
                      }
                    >
                      {isFixedRouteAddonEnabled
                        ? t('customers.features.fixedRouteOn')
                        : t('customers.features.fixedRouteOff')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
              {loadingOverrides ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {hasAnyActiveFlags && (
                    <Card variant="outlined">
                      <CardHeader
                        title={<Typography variant="subtitle2">{t('customers.features.activeFlagsTitle')}</Typography>}
                        subheader={t('customers.features.activeFlagsSubheader')}
                        sx={{ pb: 0 }}
                      />
                      <CardContent>
                        <Tabs
                          value={activeFlagsTab}
                          onChange={(_, value) => setActiveFlagsTab(value)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{ mb: 1 }}
                        >
                          {categoryTabs.map((cat) => {
                            const count = getFlagsForTab(cat.id, true).length;
                            return <Tab key={cat.id} value={cat.id} label={`${cat.label} (${count})`} />;
                          })}
                        </Tabs>
                        <CategoryCard
                          category={activeTabCategory}
                          flags={activeTabFlags}
                          getEffectiveEnabled={getEffectiveEnabled}
                          getOverrideForFlag={getOverrideForFlag}
                          onToggle={handleToggleFlag}
                          onToggleCategory={handleToggleCategoryFlags}
                        />
                      </CardContent>
                    </Card>
                  )}

                  {hasAnyInactiveFlags && (
                    <Card variant="outlined">
                      <CardHeader
                        title={<Typography variant="subtitle2">{t('customers.features.inactiveFlagsTitle')}</Typography>}
                        subheader={t('customers.features.inactiveFlagsSubheader')}
                        sx={{ pb: 0 }}
                      />
                      <CardContent>
                        <Tabs
                          value={inactiveFlagsTab}
                          onChange={(_, value) => setInactiveFlagsTab(value)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{ mb: 1 }}
                        >
                          {categoryTabs.map((cat) => {
                            const count = getFlagsForTab(cat.id, false).length;
                            return <Tab key={cat.id} value={cat.id} label={`${cat.label} (${count})`} />;
                          })}
                        </Tabs>
                        <CategoryCard
                          category={inactiveTabCategory}
                          flags={inactiveTabFlags}
                          getEffectiveEnabled={getEffectiveEnabled}
                          getOverrideForFlag={getOverrideForFlag}
                          onToggle={handleToggleFlag}
                          onToggleCategory={handleToggleCategoryFlags}
                        />
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Licenses section */}
              {(() => {
                const assigned = customerLicenses.filter((l) => l.user_id).length;
                const total = customerLicenses.length;
                const available = total - assigned;
                return (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {t('customers.licensesSection.heading')}
                        {!loadingLicenses && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            {t('customers.licensesSection.counts', { assigned, total, available })}
                          </Typography>
                        )}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Icon size={20} name="plus" />}
                          onClick={() => {
                            setAddLicensesCount(1);
                            setAddLicensesExpiry('');
                            setAddLicensesOpen(true);
                          }}
                        >
                          {t('customers.licensesSection.addLicenses')}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Icon size={20} name="user" />}
                          disabled={loadingLicenses || available === 0}
                          onClick={() => void handleOpenNextLicenseToAssign()}
                        >
                          {t('customers.licensesSection.assignLicense')}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={total === 0}
                          onClick={() => {
                            setRenewAllExpiry('');
                            setRenewAllOpen(true);
                          }}
                        >
                          {t('customers.licensesSection.renewAll')}
                        </Button>
                      </Box>
                    </Box>
                    {loadingLicenses ? (
                      <CircularProgress size={24} sx={{ mb: 2 }} />
                    ) : customerLicenses.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('customers.licensesSection.noLicenses')}
                      </Typography>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
                          <TextField
                            size="small"
                            label={t('customers.licensesSection.searchLabel')}
                            placeholder={t('customers.licensesSection.searchPlaceholder')}
                            value={licenseSearch}
                            onChange={(e) => setLicenseSearch(e.target.value)}
                            sx={{ minWidth: { xs: '100%', sm: 280 } }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {t('common.showing', { count: filteredCustomerLicenses.length, total: customerLicenses.length })}
                          </Typography>
                        </Box>
                        <TableContainer sx={{ overflowX: 'auto', mb: 2 }}>
                          <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <TableSortLabel
                                  active={licenseSortBy === 'user'}
                                  direction={licenseSortBy === 'user' ? licenseSortDirection : 'asc'}
                                  onClick={() => {
                                    if (licenseSortBy === 'user') {
                                      setLicenseSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                                    } else {
                                      setLicenseSortBy('user');
                                      setLicenseSortDirection('asc');
                                    }
                                  }}
                                >
                                  {t('customers.licensesSection.columnUser')}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>
                                <TableSortLabel
                                  active={licenseSortBy === 'expires'}
                                  direction={licenseSortBy === 'expires' ? licenseSortDirection : 'asc'}
                                  onClick={() => {
                                    if (licenseSortBy === 'expires') {
                                      setLicenseSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                                    } else {
                                      setLicenseSortBy('expires');
                                      setLicenseSortDirection('asc');
                                    }
                                  }}
                                >
                                  {t('customers.licensesSection.columnExpires')}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>
                                <TableSortLabel
                                  active={licenseSortBy === 'status'}
                                  direction={licenseSortBy === 'status' ? licenseSortDirection : 'asc'}
                                  onClick={() => {
                                    if (licenseSortBy === 'status') {
                                      setLicenseSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                                    } else {
                                      setLicenseSortBy('status');
                                      setLicenseSortDirection('asc');
                                    }
                                  }}
                                >
                                  {t('customers.licensesSection.columnStatus')}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">{t('customers.licensesSection.columnActions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredCustomerLicenses.map((lic) => {
                            const status = getLicenseStatus(lic, t);
                            return (
                              <TableRow key={lic.license_id}>
                                <TableCell>
                                  <Typography variant="body2">{lic.user_email || '\u2014'}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {new Date(lic.expires_at).toLocaleDateString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={status.label}
                                    size="small"
                                    color={status.color}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {!lic.user_id && (
                                    <Button
                                      size="small"
                                      startIcon={<Icon size={20} name="user" />}
                                      onClick={() => void openAssignLicenseDialog(lic)}
                                    >
                                      {t('customers.licensesSection.assign')}
                                    </Button>
                                  )}
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => void handleDeleteLicense(lic.license_id)}
                                  >
                                    <Icon name="trash" size={20} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          </TableBody>
                          </Table>
                        </TableContainer>
                        {filteredCustomerLicenses.length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('customers.licensesSection.noMatches')}
                          </Typography>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </Box>
      </Box>

      {/* New customer dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('customers.newCustomerDialog.title')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('customers.newCustomerDialog.nameLabel')}
            fullWidth
            value={newCustomerName}
            onChange={(e) => setNewCustomerName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={createLoading}>
            {t('common:cancel')}
          </Button>
          <Button variant="contained" onClick={() => void handleCreateCustomer()} disabled={createLoading}>
            {createLoading ? <CircularProgress size={18} /> : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add existing user to customer dialog */}
      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('customers.addUserDialog.title', { customer: selectedCustomer?.name })}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {addUserLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : unassignedUsers.length === 0 ? (
            <Typography color="text.secondary">
              {t('customers.addUserDialog.noUnassigned')}
            </Typography>
          ) : (
            <Autocomplete
              options={unassignedUsers}
              getOptionLabel={(u) => `${u.email}${u.full_name ? ` (${u.full_name})` : ''}`}
              value={selectedUserToAdd}
              onChange={(_, val) => setSelectedUserToAdd(val)}
              renderInput={(params) => (
                <TextField {...params} label={t('customers.addUserDialog.selectUser')} size="small" fullWidth />
              )}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)} disabled={addUserSaving}>{t('common:cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void handleAddUserToCustomer()}
            disabled={addUserSaving || !selectedUserToAdd}
          >
            {addUserSaving ? <CircularProgress size={18} /> : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite user dialog */}
      <Dialog open={inviteOpen} onClose={closeInviteDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{createdInvitation ? t('customers.inviteDialog.createdTitle') : t('customers.inviteDialog.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {createdInvitation ? (
            <>
              <Alert severity="success" sx={{ mb: 1 }}>
                <Trans
                  i18nKey="admin:customers.inviteDialog.sentTo"
                  values={{ email: createdInvitation.email }}
                  components={[<strong key="0" />]}
                />
              </Alert>
              <Typography variant="body2" color="text.secondary">
                {t('customers.inviteDialog.emailSentNote', {
                  date: new Date(createdInvitation.expires_at).toLocaleDateString(),
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('customers.inviteDialog.copyManual')}
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={`${window.location.origin}/register?token=${createdInvitation.token}`}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: 13 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => void handleCopyInviteLink()}>
                        {inviteCopied ? <Icon name="check" size={20} /> : <Icon name="clipboard" size={20} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : (
            <>
              <TextField
                label={t('customers.inviteDialog.emailLabel')}
                type="email"
                size="small"
                fullWidth
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>{t('customers.inviteDialog.roleLabel')}</InputLabel>
                <Select
                  value={inviteRole}
                  label={t('customers.inviteDialog.roleLabel')}
                  onChange={(e) => setInviteRole(e.target.value as 'member' | 'company_admin')}
                >
                  <MenuItem value="member">{t('customers.inviteDialog.roleMember')}</MenuItem>
                  <MenuItem value="company_admin">{t('customers.inviteDialog.roleCompanyAdmin')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('customers.inviteDialog.expiresInLabel')}</InputLabel>
                <Select
                  value={inviteExpiry}
                  label={t('customers.inviteDialog.expiresInLabel')}
                  onChange={(e) => setInviteExpiry(Number(e.target.value))}
                >
                  <MenuItem value={7}>{t('customers.inviteDialog.expires7')}</MenuItem>
                  <MenuItem value={14}>{t('customers.inviteDialog.expires14')}</MenuItem>
                  <MenuItem value={30}>{t('customers.inviteDialog.expires30')}</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {createdInvitation ? (
            <Button onClick={closeInviteDialog}>{t('common.done')}</Button>
          ) : (
            <>
              <Button onClick={closeInviteDialog} disabled={inviteSaving}>{t('common:cancel')}</Button>
              <Button variant="contained" onClick={() => void handleCreateInvitation()} disabled={inviteSaving}>
                {inviteSaving ? <CircularProgress size={18} /> : t('customers.inviteDialog.sendInvitation')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Licenses dialog */}
      <Dialog open={addLicensesOpen} onClose={() => setAddLicensesOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('customers.addLicensesDialog.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label={t('customers.addLicensesDialog.countLabel')}
            type="number"
            size="small"
            fullWidth
            inputProps={{ min: 1 }}
            value={addLicensesCount}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              if (!isNaN(val)) setAddLicensesCount(Math.max(1, val));
            }}
          />
          <TextField
            label={t('customers.addLicensesDialog.expiryLabel')}
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={addLicensesExpiry}
            onChange={(e) => setAddLicensesExpiry(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddLicensesOpen(false)} disabled={addLicensesSaving}>{t('common:cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void handleBatchCreate()}
            disabled={addLicensesSaving || !addLicensesExpiry.trim()}
          >
            {addLicensesSaving ? <CircularProgress size={18} /> : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renew All dialog */}
      <Dialog open={renewAllOpen} onClose={() => setRenewAllOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('customers.renewAllDialog.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('customers.renewAllDialog.description')}
          </Typography>
          <TextField
            label={t('customers.renewAllDialog.expiryLabel')}
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={renewAllExpiry}
            onChange={(e) => setRenewAllExpiry(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewAllOpen(false)} disabled={renewAllSaving}>{t('common:cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void handleBulkRenew()}
            disabled={renewAllSaving || !renewAllExpiry.trim()}
          >
            {renewAllSaving ? <CircularProgress size={18} /> : t('customers.licensesSection.renewAll')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign License dialog */}
      <Dialog open={!!assignLicenseTarget} onClose={() => setAssignLicenseTarget(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('customers.assignLicenseDialog.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {assignLicenseLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : assignLicenseUsers.length === 0 ? (
            <Typography color="text.secondary">
              {t('customers.assignLicenseDialog.allAssigned')}
            </Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('customers.assignLicenseDialog.userLabel')}</InputLabel>
              <Select
                value={assignLicenseUserId}
                label={t('customers.assignLicenseDialog.userLabel')}
                onChange={(e) => setAssignLicenseUserId(e.target.value)}
              >
                {assignLicenseUsers.map((u) => (
                  <MenuItem key={u.user_id} value={u.user_id}>
                    {u.email}{u.full_name ? ` (${u.full_name})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignLicenseTarget(null)} disabled={assignLicenseSaving}>{t('common:cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void handleAssignLicense()}
            disabled={assignLicenseSaving || assignLicenseLoading || assignLicenseUsers.length === 0}
          >
            {assignLicenseSaving ? <CircularProgress size={18} /> : t('customers.licensesSection.assign')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Category Card Component ──────────────────────────────────────────────────

interface CategoryCardProps {
  category: Category;
  flags: AdminFeatureFlag[];
  getEffectiveEnabled: (flag: AdminFeatureFlag) => boolean;
  getOverrideForFlag: (flag: AdminFeatureFlag) => CustomerFeatureFlagOverride | undefined;
  onToggle: (flag: AdminFeatureFlag) => void;
  onToggleCategory: (flags: AdminFeatureFlag[], enable: boolean) => void;
}

function CategoryCard({
  category,
  flags,
  getEffectiveEnabled,
  getOverrideForFlag,
  onToggle,
  onToggleCategory,
}: CategoryCardProps) {
  const { t } = useTranslation('admin');
  const enabledCount = flags.filter((f) => getEffectiveEnabled(f)).length;
  const allEnabled = enabledCount === flags.length;
  const stateOn = t('customers.features.stateOn');
  const stateOff = t('customers.features.stateOff');
  const stateNone = t('customers.features.stateNone');

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader
        avatar={
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: category.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            {category.icon}
          </Box>
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {category.label}
            </Typography>
            <Chip
              label={`${enabledCount}/${flags.length}`}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Box>
        }
        action={
          <Tooltip title={allEnabled ? t('customers.features.disableAll') : t('customers.features.enableAll')}>
            <Switch
              checked={allEnabled}
              onChange={() => onToggleCategory(flags, !allEnabled)}
              size="small"
            />
          </Tooltip>
        }
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ pt: 1 }}>
        {flags.length === 0 ? (
          <Typography variant="caption" color="text.disabled">
            {t('customers.features.noFlagsInCategory')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {flags.map((flag) => {
              const effectiveEnabled = getEffectiveEnabled(flag);
              const override = getOverrideForFlag(flag);
              return (
                <Box
                  key={flag.feature_flag_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                    px: 0.5,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Tooltip title={flag.description || flag.name} placement="top-start" enterDelay={400}>
                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'monospace', display: 'block', lineHeight: 1.3 }}
                        noWrap
                      >
                        {flag.name}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('customers.features.globalLabel', { state: flag.enabled ? stateOn : stateOff })}
                          sx={{ height: 18, fontSize: '0.62rem' }}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('customers.features.overrideLabel', {
                            state: override ? (override.enabled ? stateOn : stateOff) : stateNone,
                          })}
                          sx={{ height: 18, fontSize: '0.62rem' }}
                        />
                        <Chip
                          size="small"
                          color={effectiveEnabled ? 'success' : 'default'}
                          label={t('customers.features.effectiveLabel', { state: effectiveEnabled ? stateOn : stateOff })}
                          sx={{ height: 18, fontSize: '0.62rem' }}
                        />
                      </Box>
                    </Box>
                  </Tooltip>
                  <Switch
                    checked={effectiveEnabled}
                    onChange={() => onToggle(flag)}
                    size="small"
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
